import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem, SelectTrigger as _SelectTrigger, SelectValue as _SelectValue } from "../../components/ui/select";
import type { CrmMember, Lead } from "./types";

const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

export function AssignLeadModal({ lead, agents, currentAgentName, onClose, onSave }: {
  lead: Lead;
  agents: CrmMember[];
  currentAgentName: string;
  onClose: () => void;
  onSave: (agentId: string | null) => Promise<void>;
}) {
  const [agentId, setAgentId] = useState(lead.assignedAgentId || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (saving) return;
    setSaving(true); setError("");
    try {
      await onSave(agentId || null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to update assignment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Assign lead"
      subtitle={`${lead.name || lead.contactId || "Unnamed lead"}${lead.company ? ` · ${lead.company}` : ""} — currently ${currentAgentName}.`}
      onClose={onClose}
      width="max-w-md"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">
            Cancel
          </button>
          <button disabled={saving} onClick={submit} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
            {saving ? "Saving…" : "Save assignment"}
          </button>
        </>
      }
    >
      <label className="mb-1.5 block text-xs font-semibold text-ink-2">Assign to</label>
      <Select value={agentId || "unassigned"} onValueChange={(v: string) => setAgentId(v === "unassigned" ? "" : v)}>
        <SelectTrigger autoFocus className="w-full bg-transparent border-line focus:ring-1 focus:ring-inset focus:ring-brand text-ink outline-none px-3 py-2 text-sm rounded-lg h-auto min-h-[38px]">
          <SelectValue placeholder="Unassigned" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unassigned" className="focus:bg-soft focus:text-ink">Unassigned</SelectItem>
          {agents.map((a) => (
            <SelectItem key={a.id} value={a.id} className="focus:bg-soft focus:text-ink">
              {a.name}
              {a.presence ? ` — ${a.presence}` : ""}
              {typeof a.calls === "number" ? ` · ${a.calls} calls today` : ""}
              {!a.active ? " (disabled)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-2 text-xs text-ink-3">Reassigning keeps the full call history with the lead.</p>
      {error && <p role="alert" className="mt-3 text-xs font-semibold text-red-500">{error}</p>}
    </Modal>
  );
}
